import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSessionHeaders } from '../../lib/serverSession'

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface KYCInformation {
  id: string;
  user_id: number;
  first_name: string;
  last_name: string;
  full_legal_name: string;
  id_type: 'national_id' | 'passport' | 'drivers_license' | 'national_id_card' | 'other_government_id';
  phone_number: string;
  email_address: string;
  street_address: string;
  city: string;
  province: string;
  postal_code: string;
  country_code: string;
  country_name: string;
  data_consent_given: boolean;
  privacy_policy_accepted: boolean;
  kyc_status: 'pending' | 'completed' | 'rejected' | 'expired';
  kyc_completed_at: string;
  certificate_requested: boolean;
  certificate_generated_at?: string;
  certificate_sent_at?: string;
  created_at: string;
  updated_at: string;
  rejection_reason?: string;
  admin_notes?: string;
  last_modified_by?: string;
  kyc_editable?: boolean; // When true, admin has unlocked KYC for user editing
}

interface KYCStatusDashboardProps {
  userId: number;
  onStartKYC?: () => void;
  onUpdateKYC?: () => void;
  emptyStateDetails?: React.ReactNode;
}

export const KYCStatusDashboard: React.FC<KYCStatusDashboardProps> = ({
  userId,
  onStartKYC,
  onUpdateKYC,
  emptyStateDetails
}) => {
  const { t } = useTranslation('common');
  const [kycData, setKycData] = useState<KYCInformation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadKYCData();
  }, [userId]);

  const loadKYCData = async () => {
    try {
      setLoading(true);
      setError('');

      // Use API endpoint because magic link users don't have Supabase Auth sessions
      // and RLS policies using auth.uid() will return null for them.
      // The userId is already validated by the parent component.
      const response = await fetch(`${API_BASE_URL}/api/user/kyc-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'load' })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error loading KYC data:', result.error);
        throw new Error(result.error || 'Failed to load KYC data');
      }

      const kyc = result.data?.kyc || null
      console.log('✅ KYC data loaded for user', userId, ':', kyc ? `status=${kyc.kyc_status}` : 'no KYC record');
      setKycData(kyc);

    } catch (error) {
      console.error('Error loading KYC data:', error);
      setError('Failed to load KYC information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'rejected': return '#ef4444';
      case 'expired': return '#6b7280';
      default: return '#9ca3af';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      case 'expired': return '⏰';
      default: return '📋';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('kyc.statusBadge.approved', 'APPROVED');
      case 'pending': return t('kyc.statusBadge.underReview', 'UNDER REVIEW');
      case 'rejected': return t('kyc.statusBadge.rejected', 'REJECTED');
      case 'expired': return t('kyc.statusBadge.expired', 'EXPIRED');
      default: return status.toUpperCase();
    }
  };

  const getCompletionPercentage = () => {
    if (!kycData) return 0;
    if (kycData.kyc_status === 'completed') return 100;
    if (kycData.kyc_status === 'pending') return 90;
    if (kycData.kyc_status === 'rejected') return 75;
    return 25;
  };

  const requiredSteps = [
    { id: 'personal_info', name: t('kyc.step.personalInfo', 'Personal Information'), completed: !!kycData },
    { id: 'id_verification', name: t('kyc.step.idVerification', 'ID Verification'), completed: !!kycData?.id_type },
    { id: 'address_verification', name: t('kyc.step.addressVerification', 'Address Verification'), completed: !!kycData?.street_address },
    { id: 'consent', name: t('kyc.step.consent', 'Privacy Consent'), completed: !!kycData?.data_consent_given },
    { id: 'review', name: t('kyc.step.review', 'Review & Approval'), completed: kycData?.kyc_status === 'completed' }
  ];

  if (loading) {
    return (
      <div className="kyc-status-container" style={{
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #374151',
        textAlign: 'center'
      }}>
        <div style={{ color: '#ffffff' }}>{t('kyc.loadingStatus', 'Loading KYC status...')}</div>
      </div>
    );
  }

  return (
    <div className="kyc-status-container" style={{
      backgroundColor: 'rgba(31, 41, 55, 0.9)',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid #374151',
      marginBottom: '24px'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#f59e0b',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🆔 {t('kyc.verificationStatusHeading', 'KYC Verification Status')}
        </h2>
        
        {kycData && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${getStatusColor(kycData.kyc_status)}20`,
            border: `1px solid ${getStatusColor(kycData.kyc_status)}`,
            borderRadius: '20px',
            color: getStatusColor(kycData.kyc_status),
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {getStatusIcon(kycData.kyc_status)}
            {getStatusText(kycData.kyc_status)}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          color: '#f87171'
        }}>
          ❌ {error}
        </div>
      )}

      {!kycData ? (
        // No KYC Data - Show Getting Started
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ color: 'white', fontSize: '24px', marginBottom: '12px' }}>
            {t('kyc.empty.heading', 'Complete Your KYC Verification')}
          </h3>
          <p style={{ color: '#ffffff', fontSize: '16px', marginBottom: '24px', lineHeight: '1.6' }}>
            {t('kyc.empty.body', 'To receive your share certificates and comply with regulatory requirements, please complete the KYC (Know Your Customer) verification process.')}
          </p>

          {emptyStateDetails && (
            <div style={{ textAlign: 'left', marginBottom: '24px' }}>
              {emptyStateDetails}
            </div>
          )}

          <button
            onClick={onStartKYC}
            style={{
              padding: '16px 32px',
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3b82f6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🚀 {t('kyc.startButton', 'Start KYC Verification')}
          </button>
        </div>
      ) : (
        // KYC Data Exists - Show Status
        <>
          {/* Progress Bar */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ color: '#ffffff', fontSize: '14px' }}>{t('kyc.verificationProgress', 'Verification Progress')}</span>
              <span style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600' }}>
                {getCompletionPercentage()}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: 'rgba(55, 65, 81, 0.5)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${getCompletionPercentage()}%`,
                height: '100%',
                backgroundColor: getStatusColor(kycData.kyc_status),
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Verification Steps */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
              {t('kyc.verificationStepsHeading', 'Verification Steps')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {requiredSteps.map((step, index) => (
                <div key={step.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: step.completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(55, 65, 81, 0.5)',
                  border: step.completed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #4b5563',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: step.completed ? '#10b981' : '#6b7280',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 'bold'
                  }}>
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <span style={{
                    color: step.completed ? '#10b981' : '#9ca3af',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {step.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* KYC Information Summary */}
          <div style={{
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              📋 {t('kyc.verificationDetailsHeading', 'Verification Details')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              fontSize: '14px'
            }}>
              <div>
                <span style={{ color: '#ffffff' }}>{t('kyc.field.fullName', 'Full Name:')}</span>
                <div style={{ color: '#ffffff', fontWeight: '500' }}>{kycData.full_legal_name}</div>
              </div>
              <div>
                <span style={{ color: '#ffffff' }}>{t('kyc.field.idType', 'ID Type:')}</span>
                <div style={{ color: '#ffffff', fontWeight: '500' }}>
                  {kycData.id_type === 'passport' ? t('kyc.idType.passport', 'Passport') :
                   kycData.id_type === 'drivers_license' ? t('kyc.idType.driversLicense', "Driver's License") :
                   kycData.id_type === 'national_id_card' ? t('kyc.idType.nationalIdCard', 'National ID Card') :
                   kycData.id_type === 'other_government_id' ? t('kyc.idType.otherGovernmentId', 'Other Government-Issued ID') :
                   kycData.id_type === 'national_id' ? t('kyc.idType.nationalId', 'National ID') :
                   kycData.id_type || t('kyc.idType.notSpecified', 'Not specified')}
                </div>
              </div>
              <div>
                <span style={{ color: '#ffffff' }}>{t('kyc.field.country', 'Country:')}</span>
                <div style={{ color: '#ffffff', fontWeight: '500' }}>{kycData.country_name}</div>
              </div>
              <div>
                <span style={{ color: '#ffffff' }}>{t('kyc.field.completed', 'Completed:')}</span>
                <div style={{ color: '#ffffff', fontWeight: '500' }}>
                  {new Date(kycData.kyc_completed_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>



          {/* Rejection Details */}
          {kycData.kyc_status === 'rejected' && kycData.rejection_reason && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#f87171', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                ❌ {t('kyc.rejectedHeading', 'KYC Verification Rejected')}
              </h3>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#fca5a5', fontSize: '14px', fontWeight: '500' }}>{t('kyc.rejectionReasonLabel', 'Reason for rejection:')}</span>
                <div style={{
                  color: '#fecaca',
                  fontSize: '14px',
                  marginTop: '4px',
                  padding: '8px 12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.05)',
                  borderRadius: '6px',
                  border: '1px solid rgba(239, 68, 68, 0.2)'
                }}>
                  {kycData.rejection_reason}
                </div>
              </div>
              {kycData.last_modified_by && (
                <div style={{ fontSize: '13px', color: '#fca5a5' }}>
                  {t('kyc.reviewedByLabel', 'Reviewed by:')} {kycData.last_modified_by} • {new Date(kycData.updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {kycData.kyc_status === 'rejected' && (
              <>
                <button
                  onClick={onUpdateKYC}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f59e0b',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🔄 {t('kyc.resubmitButton', 'Resubmit KYC Documents')}
                </button>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '6px',
                  color: '#f59e0b',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  💡 {t('kyc.addressIssuesHint', 'Please address the issues mentioned above before resubmitting')}
                </div>
              </>
            )}

            {kycData.kyc_status === 'pending' && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                color: '#f59e0b',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ⏳ {t('kyc.underReviewBanner', "Your KYC is under review. You will receive an email notification once it's processed.")}
              </div>
            )}

            {/* Show Edit button when KYC is completed but admin has unlocked it for editing */}
            {kycData.kyc_status === 'completed' && kycData.kyc_editable && (
              <>
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '8px',
                  color: '#22d3ee',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  width: '100%'
                }}>
                  🔓 {t('kyc.editableBanner', 'An administrator has unlocked your KYC for editing. You can update your information below.')}
                </div>
                <button
                  onClick={onUpdateKYC}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#06b6d4',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ✏️ {t('kyc.editButton', 'Edit KYC Information')}
                </button>
              </>
            )}

          </div>
        </>
      )}
    </div>
  );
};
